import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Formation } from '@/types/Concept.types'
import type { Player } from '@/types'
import type { DrawingState, HashAlignment } from '@/types'
import { flipPlayer } from '@/utils/Flip.utils'
import { extractFormationPositions } from '@/components/canvas/FormationCanvas'
import { areLinemenAtDefaultPositions, repositionLinemenForHash } from '@/utils/Lineman.utils'
import { getDefaultColorForRole } from '@/constants/Formation.constants'
import { generateThumbnail } from '@/utils/Thumbnail'
import { secureFetch, secureJsonFetch } from '@/utils/SecureFetch'
import { useDialogAutoClose } from '@/hooks'
import {
	collectHashPositions,
	getMirrorHash,
	type FormationInput,
	type FormationPositionInput,
	type HashPositions,
} from '@/components/formations/FormationEditor.utils'

/** Default hash alignment when creating/resetting formations */
const DEFAULT_HASH_ALIGNMENT: HashAlignment = 'middle'

const EMPTY_HASH_POSITIONS: HashPositions = { left: [], middle: [], right: [] }

interface UseFormationEditorOptions {
	formationId?: string
	playbookId: string
}

interface UseFormationEditorReturn {
	// Form state
	name: string
	setName: (name: string) => void
	mirroredName: string
	setMirroredName: (name: string) => void
	autoMirror: boolean
	hashMode: boolean
	errorMessage: string | null

	// Hash alignment
	primaryHashAlignment: HashAlignment
	mirrorHashAlignment: HashAlignment
	handleHashAlignmentChange: (newHash: HashAlignment) => void
	activeDialog: 'primary' | 'mirror'
	setActiveDialog: (dialog: 'primary' | 'mirror') => void

	// Drawing state
	drawingState: DrawingState
	setDrawingState: (state: DrawingState) => void
	showColorPicker: boolean
	setShowColorPicker: (show: boolean) => void
	showHashDialog: boolean
	setShowHashDialog: (show: boolean) => void

	// Canvas integration
	externalPlayers: Player[] | undefined
	mirroredExternalPlayers: Player[] | undefined
	transformedPositions: Player[] | undefined
	primaryLinemenAtDefault: boolean
	mirrorLinemenAtDefault: boolean
	handlePlayersChange: (players: Player[]) => void
	handleMirroredPlayersChange: (players: Player[]) => void
	handleCanvasStateReady: (getState: () => { players: Player[] }) => void
	handleMirroredCanvasStateReady: (getState: () => { players: Player[] }) => void
	canvasContainerRef: React.RefObject<HTMLDivElement | null>

	// Actions
	handleHashModeChange: (mode: boolean) => void
	handleAutoMirrorToggle: () => void
	save: () => Promise<string | undefined>

	// Status
	isSaving: boolean
	isLoading: boolean
	isValid: boolean
	mode: 'create' | 'edit'

	// Existing formation data (for edit mode)
	formation: Formation | null
	linkedFormationId: string | null

	// Change tracking for autosave
	changeCounter: number
}

export function useFormationEditor({
	formationId,
	playbookId,
}: UseFormationEditorOptions): UseFormationEditorReturn {
	const mode = formationId ? 'edit' : 'create'

	// Form state
	const [name, setName] = useState('')
	const [mirroredName, setMirroredName] = useState('')
	const [autoMirror, setAutoMirror] = useState(false)
	const [hashMode, setHashMode] = useState(false)
	const [errorMessage, setErrorMessage] = useState<string | null>(null)
	const [isSaving, setIsSaving] = useState(false)
	const [isLoading, setIsLoading] = useState(!!formationId)

	// Formation data (loaded in edit mode)
	const [formation, setFormation] = useState<Formation | null>(null)
	const [linkedFormationId, setLinkedFormationId] = useState<string | null>(null)

	// Hash alignment
	const [primaryHashAlignment, setPrimaryHashAlignment] = useState<HashAlignment>(DEFAULT_HASH_ALIGNMENT)
	const [mirrorHashAlignment, setMirrorHashAlignment] = useState<HashAlignment>(DEFAULT_HASH_ALIGNMENT)
	const [activeDialog, setActiveDialog] = useState<'primary' | 'mirror'>('primary')

	// Hash positions
	const [hashPositions, setHashPositions] = useState<HashPositions>(EMPTY_HASH_POSITIONS)
	const [mirroredHashPositions, setMirroredHashPositions] = useState<HashPositions>(EMPTY_HASH_POSITIONS)

	// Drawing state
	const [drawingState, setDrawingState] = useState<DrawingState>({
		tool: 'select',
		color: '#FFFFFF',
		brushSize: 7,
		lineStyle: 'solid',
		lineEnd: 'none',
		pathMode: 'sharp',
		eraseSize: 40,
		snapThreshold: 20,
	})
	const [showColorPicker, setShowColorPicker] = useState(false)
	const [showHashDialog, setShowHashDialog] = useState(false)

	// Resolved players (edit mode)
	const [resolvedPlayers, setResolvedPlayers] = useState<Player[]>([])

	// Change counter — incremented on canvas mutations to trigger autosave
	const [changeCounter, setChangeCounter] = useState(0)

	// Refs
	const canvasContainerRef = useRef<HTMLDivElement>(null)
	const getCanvasStateRef = useRef<(() => { players: Player[] }) | null>(null)
	const getMirroredCanvasStateRef = useRef<(() => { players: Player[] }) | null>(null)
	const syncCounterRef = useRef(0)

	// Auto-close dialogs
	useDialogAutoClose({
		isOpen: showColorPicker,
		onClose: () => setShowColorPicker(false),
		dataAttribute: 'data-color-dialog',
	})

	useDialogAutoClose({
		isOpen: showHashDialog,
		onClose: () => setShowHashDialog(false),
		dataAttribute: 'data-hash-dialog',
	})

	// Reset state when formationId changes (navigating between formations without remount)
	useEffect(() => {
		setName('')
		setMirroredName('')
		setAutoMirror(false)
		setHashMode(false)
		setErrorMessage(null)
		setFormation(null)
		setLinkedFormationId(null)
		setResolvedPlayers([])
		setHashPositions(EMPTY_HASH_POSITIONS)
		setMirroredHashPositions(EMPTY_HASH_POSITIONS)
		setPrimaryHashAlignment(DEFAULT_HASH_ALIGNMENT)
		setMirrorHashAlignment(DEFAULT_HASH_ALIGNMENT)
		setIsLoading(!!formationId)
	}, [formationId])

	// Load formation data in edit mode
	useEffect(() => {
		if (!formationId) return

		const toPlayer = (p: { id: string; properties: Record<string, unknown> }): Player => ({
			id: crypto.randomUUID(),
			x: Number(p.properties.x ?? 0),
			y: Number(p.properties.y ?? 0),
			label: String(p.properties.label ?? ''),
			color: String(p.properties.color ?? '#1a1a2e'),
			isLineman: Boolean(p.properties.isLineman),
		})

		const groupByHash = (playerBlocks: { id: string; properties: Record<string, unknown> }[]): HashPositions => {
			const byHash: HashPositions = { left: [], middle: [], right: [] }
			for (const p of playerBlocks) {
				const hash = p.properties?.hashMark as 'left' | 'middle' | 'right' | null
				const player = toPlayer(p)
				if (hash && byHash[hash]) {
					byHash[hash].push(player)
				} else {
					byHash.middle.push(player)
				}
			}
			return byHash
		}

		const loadFormation = async () => {
			setIsLoading(true)
			try {
				const response = await secureFetch(`/api/formation-blocks/${formationId}`)
				if (!response.ok) throw new Error('Failed to load formation')
				const resolved = await response.json()

				// Set formation metadata
				setFormation({
					id: formationId,
					name: resolved.name ?? '',
					description: resolved.description ?? null,
					thumbnail: resolved.thumbnail ?? null,
					updated_at: new Date(),
					hashMode: resolved.hashMode ?? false,
					linked_formation_id: resolved.linked_formation_id ?? null,
				})
				setName(resolved.name ?? '')
				setLinkedFormationId(resolved.linked_formation_id ?? null)

				if (resolved.linked_formation_id) {
					setAutoMirror(true)
				}

				const playerBlocks = resolved.players || []
				setResolvedPlayers(playerBlocks.map(toPlayer))

				if (resolved.hashMode) {
					setHashMode(true)
					setHashPositions(groupByHash(playerBlocks))
				}

				// Fetch linked formation if available
				if (resolved.linked_formation_id) {
					const linkedResp = await secureFetch(`/api/formation-blocks/${resolved.linked_formation_id}`)
					if (linkedResp.ok) {
						const linkedResolved = await linkedResp.json()
						setMirroredName(linkedResolved.name ?? '')

						if (resolved.hashMode) {
							setMirroredHashPositions(groupByHash(linkedResolved.players || []))
						}
					}
				}
			} catch (err) {
				console.error('Failed to load formation data:', err)
				setErrorMessage('Failed to load formation data.')
			} finally {
				setIsLoading(false)
			}
		}

		loadFormation()
	}, [formationId])

	// Transform resolved players for non-hash mode edit
	const transformedPositions = useMemo(() => {
		if (mode !== 'edit' || resolvedPlayers.length === 0) return undefined
		if (formation?.hashMode) return undefined
		return resolvedPlayers
	}, [mode, resolvedPlayers, formation?.hashMode])

	// Canvas state refs
	const handleCanvasStateReady = useCallback((getState: () => { players: Player[] }) => {
		getCanvasStateRef.current = getState
	}, [])

	const handleMirroredCanvasStateReady = useCallback((getState: () => { players: Player[] }) => {
		getMirroredCanvasStateRef.current = getState
	}, [])

	// Hash mode toggle
	const handleHashModeChange = useCallback((newHashMode: boolean) => {
		if (newHashMode) {
			const currentPlayers = getCanvasStateRef.current?.().players || []
			setHashPositions({
				left: currentPlayers.map(p => ({ ...p })),
				middle: currentPlayers.map(p => ({ ...p })),
				right: currentPlayers.map(p => ({ ...p })),
			})
		}
		setHashMode(newHashMode)
	}, [])

	// Hash alignment change
	const handleHashAlignmentChange = useCallback((newHash: HashAlignment) => {
		const currentAlignment = activeDialog === 'primary' ? primaryHashAlignment : mirrorHashAlignment
		const setCurrentAlignment = activeDialog === 'primary' ? setPrimaryHashAlignment : setMirrorHashAlignment

		if (!hashMode) {
			setCurrentAlignment(newHash)
			return
		}

		const canvasRef = activeDialog === 'primary' ? getCanvasStateRef : getMirroredCanvasStateRef
		const setCurrentPositions = activeDialog === 'primary' ? setHashPositions : setMirroredHashPositions
		const currentPlayers = canvasRef.current?.().players || []

		setCurrentPositions(prev => {
			const updated = {
				...prev,
				[currentAlignment]: currentPlayers.map(p => ({ ...p })),
			}
			const newHashPlayers = updated[newHash]
			const repositionedPlayers = repositionLinemenForHash(newHashPlayers, newHash)
			return {
				...updated,
				[newHash]: repositionedPlayers,
			}
		})

		setCurrentAlignment(newHash)
	}, [hashMode, activeDialog, primaryHashAlignment, mirrorHashAlignment])

	// External players for canvas
	const externalPlayers = useMemo(() => {
		return hashMode ? hashPositions[primaryHashAlignment] : undefined
	}, [hashMode, primaryHashAlignment, hashPositions])

	const mirroredExternalPlayers = useMemo(() => {
		return hashMode ? mirroredHashPositions[mirrorHashAlignment] : undefined
	}, [hashMode, mirrorHashAlignment, mirroredHashPositions])

	// Linemen position checks
	const primaryLinemenAtDefault = useMemo(() => {
		const players = hashMode ? hashPositions[primaryHashAlignment] : []
		return areLinemenAtDefaultPositions(players, primaryHashAlignment)
	}, [hashMode, hashPositions, primaryHashAlignment])

	const mirrorLinemenAtDefault = useMemo(() => {
		const players = hashMode ? mirroredHashPositions[mirrorHashAlignment] : []
		return areLinemenAtDefaultPositions(players, mirrorHashAlignment)
	}, [hashMode, mirroredHashPositions, mirrorHashAlignment])

	const mirrorPlayers = (players: Player[]): Player[] => players.map(flipPlayer)

	// Primary canvas player changes
	const handlePlayersChange = useCallback((players: Player[]) => {
		setChangeCounter(c => c + 1)

		if (!hashMode) return

		if (syncCounterRef.current > 0) {
			syncCounterRef.current--
			return
		}

		const mirrorHash = getMirrorHash(primaryHashAlignment)

		setHashPositions(prev => ({
			...prev,
			[primaryHashAlignment]: players.map(p => ({ ...p })),
		}))

		if (autoMirror) {
			syncCounterRef.current++
			setMirroredHashPositions(prev => ({
				...prev,
				[mirrorHash]: mirrorPlayers(players),
			}))
		}
	}, [hashMode, primaryHashAlignment, autoMirror])

	// Mirrored canvas player changes
	const handleMirroredPlayersChange = useCallback((players: Player[]) => {
		setChangeCounter(c => c + 1)

		if (!hashMode) return

		if (syncCounterRef.current > 0) {
			syncCounterRef.current--
			return
		}

		const primaryHash = getMirrorHash(mirrorHashAlignment)

		setMirroredHashPositions(prev => ({
			...prev,
			[mirrorHashAlignment]: players.map(p => ({ ...p })),
		}))

		if (autoMirror) {
			syncCounterRef.current++
			setHashPositions(prev => ({
				...prev,
				[primaryHash]: mirrorPlayers(players),
			}))
		}
	}, [hashMode, mirrorHashAlignment, autoMirror])

	// Auto-mirror toggle
	const handleAutoMirrorToggle = useCallback(() => {
		if (!autoMirror) {
			setAutoMirror(true)
			setActiveDialog('primary')
			setMirroredName(`${name} (Mirrored)`)
			setHashMode(true)

			const currentPlayers = getCanvasStateRef.current?.().players || []
			const isEmpty = hashPositions.left.length === 0 &&
				hashPositions.middle.length === 0 &&
				hashPositions.right.length === 0

			const playersToMirror = isEmpty
				? {
					left: currentPlayers.map(p => ({ ...p })),
					middle: currentPlayers.map(p => ({ ...p })),
					right: currentPlayers.map(p => ({ ...p })),
				}
				: hashPositions

			if (isEmpty) {
				setHashPositions(playersToMirror)
			}

			setMirroredHashPositions({
				left: mirrorPlayers(playersToMirror.right),
				middle: mirrorPlayers(playersToMirror.middle),
				right: mirrorPlayers(playersToMirror.left),
			})
		} else {
			setAutoMirror(false)
			setActiveDialog('primary')
			setMirroredName('')
		}
	}, [autoMirror, name, hashPositions])

	// Validation
	const trimmedName = (name ?? '').trim()
	const trimmedMirrorName = (mirroredName ?? '').trim()
	const isValid = autoMirror
		? trimmedName.length > 0 && trimmedMirrorName.length > 0 && trimmedName !== trimmedMirrorName
		: trimmedName.length > 0

	// Save — returns the formation ID for create-then-navigate
	const save = useCallback(async (): Promise<string | undefined> => {
		if (!isValid) return undefined

		setIsSaving(true)
		setErrorMessage(null)

		try {
			// Generate thumbnail
			let thumbnail: string | null = null
			if (canvasContainerRef.current) {
				try {
					thumbnail = await generateThumbnail(canvasContainerRef.current)
				} catch {
					// Thumbnail generation is non-critical
				}
			}

			// Build fresh positions including current canvas state
			const currentPlayers = getCanvasStateRef.current?.().players || []
			let freshHashPositions = hashPositions
			let freshMirroredHashPositions = mirroredHashPositions

			if (hashMode) {
				freshHashPositions = {
					...hashPositions,
					[primaryHashAlignment]: currentPlayers.map(p => ({ ...p })),
				}

				if (autoMirror) {
					const currentMirroredPlayers = getMirroredCanvasStateRef.current?.().players || []
					freshMirroredHashPositions = {
						...mirroredHashPositions,
						[mirrorHashAlignment]: currentMirroredPlayers.map(p => ({ ...p })),
					}
				}

				setHashPositions(freshHashPositions)
				if (autoMirror) {
					setMirroredHashPositions(freshMirroredHashPositions)
				}
			}

			// Auto-mirror mode (linked pair)
			if (autoMirror) {
				const primaryPositions = collectHashPositions(freshHashPositions)
				const mirroredPositions = collectHashPositions(freshMirroredHashPositions)

				const formationA: FormationInput = {
					name: name.trim(),
					thumbnail,
					hashMode: true,
					positions: primaryPositions,
				}

				const formationB: FormationInput = {
					name: mirroredName.trim(),
					thumbnail: null,
					hashMode: true,
					positions: mirroredPositions,
				}

				if (mode === 'edit' && formationId && linkedFormationId) {
					// Update existing linked pair
					const response = await secureJsonFetch('/api/formation-blocks/linked-pair', {
						method: 'PUT',
						body: {
							formationAId: formationId,
							formationBId: linkedFormationId,
							formationA,
							formationB,
						},
					})
					if (!response.ok) throw new Error('Failed to update linked pair')
				} else if (mode === 'edit' && formationId && !linkedFormationId) {
					// Convert to linked pair
					const response = await secureJsonFetch(`/api/formation-blocks/${formationId}/convert-to-linked`, {
						method: 'POST',
						body: { formationA, formationB },
					})
					if (!response.ok) throw new Error('Failed to convert to linked pair')
				} else {
					// Create new linked pair
					const response = await secureJsonFetch(`/api/playbooks/${playbookId}/formation-blocks/linked-pair`, {
						method: 'POST',
						body: { formationA, formationB },
					})
					if (!response.ok) throw new Error('Failed to create linked pair')
					const linkedResult = await response.json()
					return linkedResult.formationA?.id ?? linkedResult.id
				}
				return formationId
			}

			// Standard single formation save
			let positionsData: FormationPositionInput[]

			if (hashMode) {
				positionsData = collectHashPositions(freshHashPositions)
			} else {
				const players = getCanvasStateRef.current?.().players || []
				const positions = extractFormationPositions(players)
				positionsData = positions.map((pos): FormationPositionInput => ({
					role: pos.label || '',
					x: pos.x,
					y: pos.y,
					color: pos.color || getDefaultColorForRole(pos.label || ''),
					hashRelative: false,
					hashMark: null,
				}))
			}

			const formationData: FormationInput = {
				name: name.trim(),
				description: undefined,
				thumbnail,
				hashMode,
				positions: positionsData,
			}

			if (mode === 'edit' && formationId) {
				const response = await secureJsonFetch(`/api/formation-blocks/${formationId}`, {
					method: 'PUT',
					body: formationData,
				})
				if (!response.ok) throw new Error('Failed to update formation')
				return formationId
			} else {
				const response = await secureJsonFetch(`/api/playbooks/${playbookId}/formation-blocks`, {
					method: 'POST',
					body: formationData,
				})
				if (!response.ok) throw new Error('Failed to create formation')
				const createResult = await response.json()
				return createResult.id
			}
		} catch (error) {
			const errorMsg = error instanceof Error ? error.message : String(error)
			if (
				errorMsg.includes('formations_playbook_name_unique') ||
				errorMsg.includes('already exists')
			) {
				setErrorMessage(`A formation named "${name.trim()}" already exists in this playbook.`)
			} else {
				setErrorMessage('Failed to save formation. Please try again.')
			}
			throw error
		} finally {
			setIsSaving(false)
		}
	}, [
		isValid, hashPositions, mirroredHashPositions, hashMode, autoMirror,
		primaryHashAlignment, mirrorHashAlignment, name, mirroredName,
		mode, formationId, linkedFormationId, playbookId,
	])

	return {
		name,
		setName,
		mirroredName,
		setMirroredName,
		autoMirror,
		hashMode,
		errorMessage,
		primaryHashAlignment,
		mirrorHashAlignment,
		handleHashAlignmentChange,
		activeDialog,
		setActiveDialog,
		drawingState,
		setDrawingState,
		showColorPicker,
		setShowColorPicker,
		showHashDialog,
		setShowHashDialog,
		externalPlayers,
		mirroredExternalPlayers,
		transformedPositions,
		primaryLinemenAtDefault,
		mirrorLinemenAtDefault,
		handlePlayersChange,
		handleMirroredPlayersChange,
		handleCanvasStateReady,
		handleMirroredCanvasStateReady,
		canvasContainerRef,
		handleHashModeChange,
		handleAutoMirrorToggle,
		save,
		isSaving,
		isLoading,
		isValid,
		mode,
		formation,
		linkedFormationId,
		changeCounter,
	}
}
